/* eslint-disable react/no-unstable-nested-components */
import classnames from 'classnames';
import { memo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ProgressLoading, OverlayLoading } from '@/shared/ui';
import './styles.scss';
import { RHYTHM_EVENT_TAB_ID } from '@/entities/report';
import FallbackError from '@/features/reload-report-tab';
import { UIEffect, DataEffect } from '@/composers';
import LeftColumn from './leftColumn';
import RhythmEventsModal from './modal';
import WrapReport from './wrapReport';
import SummaryFunction from './summaryFunction';
import useRhythmEvents from './useRhythmEvents';

export type RhythmEventsProps = {
  activeTab: string;
  tabRef: any;
};

/**
 * Renders the ECG rhythm events tab, gating the full view behind AI processing, IndexedDB load and beat download readiness. Shows a progress loader alongside the summary function until useRhythmEvents reports readiness, then mounts the store data and UI effects, the tab modals, left column and report body inside an error boundary falling back to FallbackError.
 *
 * @param props - Tab inputs including the active tab, the tab ref exposed to the report shell and the flags forwarded to useRhythmEvents.
 * @returns The rhythm events tab content, or a progress loading view while the data is still being prepared.
 */
const RhythmEvents = ({ activeTab, tabRef }: RhythmEventsProps) => {
  const {
    keyRecoil,
    isStartedAiProcessing,
    isShowTabHeader,
    studyId,
    profileId,
    isNotEdit,
    isLoadedIndexDB,
    isCompletedDownloadBeat,
    isTabActive,
    hasVisitTab,
  } = useRhythmEvents({ activeTab });

  if (
    !isStartedAiProcessing ||
    !hasVisitTab.current ||
    !isLoadedIndexDB ||
    !isCompletedDownloadBeat
  ) {
    return (
      <>
        <ProgressLoading isStartedAiProcessing={isStartedAiProcessing} />
        <SummaryFunction
          ref={tabRef}
          studyId={studyId}
          profileId={profileId}
          isNotEdit={isNotEdit}
        />
      </>
    );
  }

  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <FallbackError
          error={error}
          resetErrorBoundary={resetErrorBoundary}
          isShowTabHeader={isShowTabHeader}
        />
      )}
    >
      <div
        id={RHYTHM_EVENT_TAB_ID}
        className={classnames('rhythm-events-tab', isShowTabHeader ? '' : '--hide-tab')}
      >
        <OverlayLoading isVisible={!isTabActive} />
        {isTabActive && (
          <>
            <DataEffect tab={activeTab} keyRecoil={keyRecoil} />
            <UIEffect tab={activeTab} keyRecoil={keyRecoil} />
            <RhythmEventsModal />
            <SummaryFunction
              ref={tabRef}
              studyId={studyId}
              profileId={profileId}
              isNotEdit={isNotEdit}
            />
            <LeftColumn />
            <WrapReport />
          </>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default memo(RhythmEvents);
